// @givmo/design-system — the app shell's mobile nav drawer, built once for every shell (OM-13).
//
// Promoted here from `apps/charity` (CPA-UIX2), which was itself a copy of `apps/super-admin`'s
// (SAPD-WEB6a). That copy's own header named the trigger for this move verbatim — "a third adopter is
// the point at which this should be promoted to a shared package instead of copied again" — and
// `apps/company` is that third adopter. The charity copy was the strict behavioural SUPERSET (it adds
// {@link ownsKey}), so promoting it costs the other adopter nothing.
//
// ⚠ THERE IS ONE IMPLEMENTATION AND THERE MUST STAY ONE. A fourth copy in an app is the defect this
// module exists to end; `apps/admin` still ships the fixed 256px sidebar and is the fourth adopter
// when its own row comes up.
//
// ══ THE SHAPE ══════════════════════════════════════════════════════════════════════════════════
//
// Below Tailwind's `lg` the sidebar does not get a mobile TWIN — it IS the drawer. One element stays
// in the DOM at every width, slid off-canvas by CSS and re-entered as an overlay when the hamburger
// opens it. That is deliberate: a shell's `menu-*` testids are an e2e + RTL contract, and a second
// copy of the nav for phones would be a second place for that contract to drift — and would make
// `getByTestId` ambiguous the moment both rendered at once. It is also why the design system's own
// `Sheet` is NOT the mechanism here: a Base-UI Dialog panel renders the nav a second time.
//
// CSS owns geometry and visibility, and {@link navDrawerClasses} publishes that recipe so four
// adopters cannot drift on it. `invisible` on the closed drawer is load-bearing rather than cosmetic:
// it takes the off-canvas links out of the tab order and the accessibility tree without a single line
// of JS, so a phone keyboard user never tabs into a menu they cannot see.
//
// This hook owns only what CSS cannot: Escape, the body scroll lock, the focus trap, returning focus
// to the hamburger, and closing when the viewport crosses INTO desktop — where the same element
// simply reads as the ordinary sidebar again, so a still-`open` state would leave a scroll lock and a
// focus trap running over a plain sidebar. That crossing is also why closing has two focus
// destinations rather than one: the hamburger it came from does not exist at `lg`.
//
// ══ WHAT AN ADOPTER STILL OWNS ═════════════════════════════════════════════════════════════════
//
// The markup, because it is the shell's: the drawer element (with `role={open ? 'dialog' :
// 'navigation'}` — see below), the hamburger, the in-drawer close button, the scrim, `inert` on the
// main column, and `onClick={close}` on every destination. This module owns the behaviour and the
// class recipe; it renders nothing.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { FOCUS_RING } from '../lib/focus';

/**
 * Tailwind's `lg` (64rem / 1024px) — the width at which the drawer IS the sidebar. Keep in step with
 * the `lg:` classes in {@link navDrawerClasses}; this is the same threshold expressed to JS.
 *
 * ⚠ IT IS THE ONLY LAYOUT BREAKPOINT IN THIS PATTERN. There is no rail between phone and desktop in
 * the estate, and a rail would be a second layout inside the shared machinery that every adopter
 * would inherit or fork. 768 is below `lg`, so a tablet gets the drawer and a full-width column.
 */
export const DESKTOP_MEDIA_QUERY = '(min-width: 64rem)';

/**
 * The one value this hook ever writes to `document.body.style.overflow`. The close path compares
 * against it before restoring, so the drawer only ever gives back a lock it still holds.
 */
const LOCKED_OVERFLOW = 'hidden';

/** Everything the browser would tab to inside the drawer. */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * A ≥44px square control (Apple/WCAG touch floor) in the `IconButton` ghost recipe, `lg:hidden` so it
 * leaves the flex flow entirely on desktop.
 *
 * Published with the hook because the design system's `IconButton` tops out at 36px — right for a
 * desktop toolbar and too small for the controls a phone admin actually taps — and because the two
 * controls that need it (the hamburger and the in-drawer close) exist only because of this pattern.
 */
export const NAV_DRAWER_TOUCH_TARGET = [
  'flex size-44 shrink-0 items-center justify-center rounded-md text-icon outline-none transition-colors',
  'hover:bg-surface-hover hover:text-text-secondary',
  // ⚠ THE RING ALONE IS 1.23:1 — see `lib/focus.ts`. These two controls are a phone admin's only way
  // into the navigation, so a focus state they cannot see is the whole keyboard path.
  FOCUS_RING,
  'lg:hidden',
].join(' ');

/**
 * The drawer element's class list — the geometry, the transition and the desktop unwind.
 *
 * ⚠ TWO TAILWIND-V4 TRAPS ARE BAKED IN HERE, which is the whole reason this is published rather than
 * copied into each shell (`agent_docs/build_and_ci.md` § Property-name traps):
 *
 *   1. `transition-[translate]`, NEVER `transition-[transform]`. Tailwind 4 compiles
 *      `-translate-x-full` / `translate-x-0` to the standalone `translate` property, so a
 *      transform-named transition names a property nothing writes — the drawer snaps, and on close
 *      the still-running `visibility` transition leaves the off-canvas links focusable for its whole
 *      duration. (Spelling the wrong utility out even in a comment materialises a dead rule, because
 *      Tailwind scans source as plain text.)
 *   2. `lg:translate-none`, NEVER the `-x-0` form. `-0` still sets a translate VALUE, which makes the
 *      desktop sidebar a containing block for `position: fixed` descendants. Same pixels, latent trap.
 *
 * And one CSS subtlety that is load-bearing for FOCUS: `visibility` is transitioned only TOWARD
 * closed. The transition-* values that govern a style change are the after-change style's, so the
 * per-state lists below are the mechanism. Toward closed it holds the links visible through the
 * slide-out and only then lets `invisible` land, untabbable. Toward open it must be ABSENT: a
 * visibility transition holds its FROM value at progress 0, so the drawer would still be
 * computed-hidden at the exact moment this hook's one-shot `focus()` runs — and a hidden element
 * refuses focus, silently. Applied instantly, open looks identical (hidden→visible computes `visible`
 * from the first tick anyway).
 */
export function navDrawerClasses(open: boolean): string {
  return [
    'fixed left-0 top-0 z-50 flex h-screen w-[256px] shrink-0 flex-col border-r border-border-subtle bg-surface',
    'duration-200 ease-out motion-reduce:transition-none',
    open
      ? 'visible translate-x-0 transition-[translate]'
      : 'invisible -translate-x-full transition-[translate,visibility]',
    'lg:visible lg:sticky lg:left-auto lg:z-auto lg:translate-none lg:transition-none',
  ].join(' ');
}

export interface NavDrawer {
  /** Whether the overlay drawer is open. Only ever true below `lg` — see the desktop-crossing effect. */
  open: boolean;
  /** Toggle from the hamburger. */
  toggle: () => void;
  /** Close — the backdrop, Escape, and every destination that navigates this document away. */
  close: () => void;
  /** The element that is the sidebar on desktop and the drawer on a phone. */
  drawerRef: RefObject<HTMLDivElement | null>;
  /** The hamburger, so focus can be handed back to it when the drawer closes. */
  triggerRef: RefObject<HTMLButtonElement | null>;
}

/**
 * The drawer's focusable stops, RENDERED ones only.
 *
 * ⚠ THE VISIBILITY FILTER IS DEFENSE IN DEPTH, and it is correct rather than incidental.
 * `querySelectorAll` still matches a `display: none` element, so a `lg:`-only control inside the
 * drawer would be found by the open effect's one-shot `.at(0)?.focus()`, silently no-op, and leave
 * the drawer open with the caret on `<body>`. No shipped adopter puts an `lg:`-gated control first or
 * last today, so nothing currently depends on this — but the next adopter that adds one re-creates
 * the condition, and the trap is invisible until someone opens a drawer on a phone.
 *
 * `getClientRects()` is the complete browser answer — empty for `display: none` on the element OR on
 * any ancestor, and it needs no `checkVisibility` support (jsdom has none).
 *
 * ⚠ AND IT STANDS DOWN WITHOUT LAYOUT. A layout-less DOM — jsdom, which every shell's drawer suite
 * runs in — reports zero rects for EVERY element, drawer included, so filtering there would empty the
 * list and take the opening focus and the Tab trap down with it: a guard against a hypothetical
 * hidden control would have broken the focus trap outright in the only environment that tests it. The
 * drawer's own rect count is the signal, because it is unrendered exactly when nothing is measurable.
 */
function focusableWithin(drawer: HTMLElement): HTMLElement[] {
  const all = [...drawer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)];
  if (drawer.getClientRects().length === 0) return all;
  return all.filter((element) => element.getClientRects().length > 0);
}

/**
 * Whether the DRAWER — rather than something open on top of it — should act on this key.
 *
 * A shell's org switcher lives inside the drawer but its menu is portalled to <body>, so while that
 * menu is open the key event's target is outside this element. The innermost open surface owns the key.
 *
 * ★ TAB is what this actually exists for. Base UI already stops Escape reaching this listener, so the
 * dropdown closes and the drawer stands regardless. Tab is not stopped — and while the menu holds
 * focus the active element is outside the drawer, which is precisely the condition the trap below
 * reads as "focus escaped, pull it back". Left to fire it preventDefaults the Tab, drags focus to the
 * top of the nav, and leaves the menu open around a caret that is no longer in it. Standing down hands
 * Tab back to Base UI's own roving focus, which closes the menu and advances normally.
 *
 * `<body>` and the document element are NOT "something else": a backdrop tap moves focus there, and the
 * drawer still has to answer Escape afterwards.
 *
 * ⚠ ITS COMPANION IS THE SCRIM'S `onMouseDown` preventDefault, which lives in each shell's markup.
 * Without it Chrome focuses the scrim button on mousedown, this reads the scrim as "something else",
 * and the next Escape is swallowed. An adopter that renders a scrim must carry that line.
 */
function ownsKey(drawer: HTMLElement | null, target: EventTarget | null): boolean {
  if (drawer === null) return false;
  if (!(target instanceof Node)) return true;
  if (target === document.body || target === document.documentElement) return true;
  return drawer.contains(target);
}

export function useNavDrawer(): NavDrawer {
  const [open, setOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  /**
   * Set by the desktop-crossing effect just before it closes, so the close path below can tell a
   * dismissal (focus belongs back on the hamburger) from a resize (the hamburger no longer exists).
   */
  const closedByDesktopCrossing = useRef(false);

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((wasOpen) => !wasOpen), []);

  // Scroll lock + the focus handoff, both undone by the same cleanup so an unmount mid-open cannot
  // strand the page with `overflow: hidden`.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = LOCKED_OVERFLOW;
    const opened = drawerRef.current;
    // One shot, no retry — sound only because the class recipe transitions `visibility` toward CLOSED
    // alone. See navDrawerClasses.
    if (opened !== null) focusableWithin(opened).at(0)?.focus();
    return () => {
      // Give back only a lock we still hold. If something else has since set its own value (an org
      // menu opening over the drawer, say), the `previousOverflow` captured at open is stale and
      // restoring it would unlock the page underneath whoever owns it now.
      if (document.body.style.overflow === LOCKED_OVERFLOW) {
        document.body.style.overflow = previousOverflow;
      }
      const drawer = drawerRef.current;
      if (closedByDesktopCrossing.current) {
        closedByDesktopCrossing.current = false;
        // The hamburger is `lg:hidden` at this width, so handing focus back to it silently no-ops and
        // drops the caret to <body>. The sidebar is the thing that survives the crossing — visible, in
        // flow, and holding the same first stop the drawer opened on. Routed through the same filtered
        // helper, so the two paths cannot disagree about what "first" means.
        if (drawer !== null) focusableWithin(drawer).at(0)?.focus();
        return;
      }
      // Focus would otherwise be lost to <body> the instant the drawer turns `invisible`.
      triggerRef.current?.focus();
    };
  }, [open]);

  // Escape closes; Tab is trapped inside the drawer. Bound to the document rather than the drawer so a
  // stray focus outside it (a click on the backdrop, say) is still pulled back in on the next Tab.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' && event.key !== 'Tab') return;
      if (!ownsKey(drawerRef.current, event.target)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        return;
      }
      const drawer = drawerRef.current;
      if (drawer === null) return;
      const focusable = focusableWithin(drawer);
      const first = focusable.at(0);
      const last = focusable.at(-1);
      if (first === undefined || last === undefined) return;
      const active = document.activeElement;
      const outside = !drawer.contains(active);
      if (event.shiftKey ? active === first || outside : active === last || outside) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // Crossing into desktop: the CSS turns the drawer back into the sidebar on its own, so the only thing
  // left to undo is this state — otherwise the scroll lock and the trap outlive the overlay.
  useEffect(() => {
    if (!open) return;
    if (typeof window.matchMedia !== 'function') return;
    const desktop = window.matchMedia(DESKTOP_MEDIA_QUERY);
    if (desktop.matches) {
      closedByDesktopCrossing.current = true;
      setOpen(false);
      return;
    }
    function onChange(event: MediaQueryListEvent) {
      if (!event.matches) return;
      closedByDesktopCrossing.current = true;
      setOpen(false);
    }
    desktop.addEventListener('change', onChange);
    return () => desktop.removeEventListener('change', onChange);
  }, [open]);

  return { open, toggle, close, drawerRef, triggerRef };
}
